
import { getSelectedBursts } from "@/helpers/store";
import { defineComponent, onMounted, reactive, toRefs, watchEffect } from "vue";
import { fetchBurstDataArray } from "@/helpers/helpers";
import * as d3 from "d3";
import VueFeather from "vue-feather";
import * as Fili from "fili";
import { SingleBurst } from "@/interfaces/Burst";

interface AnimationData {
  BatchID: number;
  data: {
    blade: string;
    data: {
      x: number[][];
      y: number[][];
      z: number[][];
    };
  }[];
}

export default defineComponent({
  components: { VueFeather },
  setup() {
    const state = reactive({
      selectedPages: getSelectedBursts(),
      selectedBatches: null as null | number[],
      data: null as null | AnimationData[],
      isLoading: true,
      sensors: null as null | string[],
      selectedAxis: "x" as "x" | "y" | "z",
      selectedBatch: 12281 as number | null,
      selectedBlade: "C" as string | null,
      lineHistory: false,
      bladesList: undefined as string[] | undefined,
    });

    onMounted(async () => {
      await refreshData();
    });

    const refreshData = async () => {
      state.isLoading = true;
      const selectedBursts = await fetchBurstDataArray(getSelectedBursts().value);
      const batches = [...new Set(selectedBursts.map((burst) => burst.BatchID))].map((batchNumber) => {
        const batchBurstList = selectedBursts.filter((burst) => burst.BatchID === batchNumber);
        return {
          BatchID: batchNumber,
          BatchName: batchBurstList[0].BatchName,
          BatchTags: batchBurstList[0].BatchTags,
          Bursts: batchBurstList,
          StartTime: batchBurstList[0].StartTime,
        };
      });

      const sampleRate = selectedBursts[0].SampleRate;

      //  Instance of a filter coefficient calculator
      const iirCalculator = new Fili.CalcCascades();

      // calculate filter coefficients
      const highpassFilterCoeffs = iirCalculator.highpass({
        order: 3, // cascade 3 biquad filters (max: 12)
        characteristic: "butterworth",
        Fs: sampleRate, // sampling frequency
        Fc: 2, // cutoff frequency / center frequency for bandpass, bandstop, peak
        gain: 0, // gain for peak, lowshelf and highshelf
        preGain: false, // adds one constant multiplication for highpass and lowpass
        // k = (1 + cos(omega)) * 0.5 / k = 1 with preGain == false
      });
      const lowpassFilterCoeffs = iirCalculator.lowpass({
        order: 3, // cascade 3 biquad filters (max: 12)
        characteristic: "butterworth",
        Fs: sampleRate, // sampling frequency
        Fc: 15, // cutoff frequency / center frequency for bandpass, bandstop, peak
        gain: 0, // gain for peak, lowshelf and highshelf
        preGain: false, // adds one constant multiplication for highpass and lowpass§
      });

      const highpassFilter = new Fili.IirFilter(highpassFilterCoeffs);
      const lowpassFilter = new Fili.IirFilter(lowpassFilterCoeffs);

      state.data = batches.map(({ BatchID, Bursts }) => ({
        BatchID: BatchID,
        data: [...new Set(Bursts.map((burst) => burst.SensorTags.Blade))].map((blade) => {
          const bladeBursts = Bursts.filter(
            (burst) =>
              burst.SensorTags.Blade === blade && burst.SensorTags.Location !== "4" && burst.SensorTags.Location !== "6"
          ).sort((a, b) => (a.SensorTags.Location > b.SensorTags.Location ? 1 : -1)); //need to test this and perhaps change the numbers or the direction of the inequality
          return {
            blade: blade,
            data: {
              x: filterIntegrateTranspose(bladeBursts, highpassFilter, lowpassFilter, "X", sampleRate),
              y: filterIntegrateTranspose(bladeBursts, highpassFilter, lowpassFilter, "Y", sampleRate),
              z: filterIntegrateTranspose(bladeBursts, highpassFilter, lowpassFilter, "Z", sampleRate),
            },
          };
        }),
      }));
      state.selectedBatches = state.data.map((batch) => batch.BatchID);
      state.isLoading = false;
      makeSVGPlot();
    };

    interface Filter {
      reinit: () => void;
      multiStep: (a: number[]) => number[];
    }

    const getDistance = (array: number[], sampleRate: number) =>
      array.map((element) => (element * 9.81 * 1000) / (sampleRate * sampleRate));

    const filterIntegrateTranspose = (
      bladeBursts: SingleBurst[],
      highpassFilter: Filter,
      lowpassFilter: Filter,
      axis: "X" | "Y" | "Z",
      sampleRate: number
    ) =>
      transposeMatrix(
        bladeBursts.map(({ Data }) => {
          highpassFilter.reinit();
          lowpassFilter.reinit();
          return getDistance(
            integrate(integrate(highpassFilter.multiStep(lowpassFilter.multiStep(Data[axis])))),
            sampleRate
          );
        })
      );

    const integrate = (data: number[]) => {
      const newData = [...data];
      newData.forEach((value, index) => (newData[index] = index === 0 ? value : value + newData[index - 1]));
      return newData;
    };

    const getBatch = () => (state.data ? state.data.find((batch) => batch.BatchID === state.selectedBatch) : undefined);

    const getBlade = (batch: AnimationData) => batch.data.find((blade) => blade.blade === state.selectedBlade);

    const getBlades = (batch: AnimationData) => {
      if (batch.data !== undefined) return batch.data.map((blade) => blade.blade);
    };

    const transposeMatrix = (matrix: number[][]) =>
      matrix.reduce(
        (acc: number[][], next: number[]) => next.map((_, i) => [...(acc[i] || ([] as number[])), next[i]]),
        [] as number[][]
      );

    const makeSVGPlot = () => {
      if (state.data === null) {
        return;
      }
      const batch = getBatch();
      if (batch === undefined) {
        return;
      }
      const blade = getBlade(batch);
      if (blade === undefined) {
        return;
      }
      const data = blade.data[state.selectedAxis];
      const dataFlat = data.flat();
      const [minYValue, maxYValue] = [d3.min(dataFlat), d3.max(dataFlat)];
      if (minYValue === undefined || maxYValue === undefined) {
        return;
      }
      const [minYScale, maxYScale] = [minYValue, maxYValue];
      const distances = [2, 40, 50, 58];
      const [minXScale, maxXScale] = [0, distances[distances.length - 1]];

      const parentNode = d3.select("#d3-animation");
      parentNode.selectChildren().remove();

      const windowHeight = window.visualViewport.height * 0.95;
      const windowWidth = window.visualViewport.width * 0.95;

      const margin = { top: 10, right: 0, bottom: 0, left: 25 };
      // Create main svg element.
      const svg = parentNode
        .append("svg")
        .attr("width", outerWidth)
        .attr("height", windowHeight)
        .append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      // Create scales and axes
      const x = d3.scaleLinear().domain([minXScale, maxXScale]).range([0, windowWidth]).nice();
      const y = d3
        .scaleLinear()
        .domain([minYScale, maxYScale])
        .range([windowHeight * 0.8, 0])
        .nice();
      const xAxis = d3.axisBottom(x);
      const yAxis = d3.axisLeft(y);

      // Render axes
      svg
        .append("g")
        .attr("class", "x axis")
        .attr("transform", "translate(0," + y(0) + ")")
        .call(xAxis);
      svg.append("g").attr("class", "y axis").call(yAxis);

      const getCoordinates = (arr: number[]): [number, number][] => arr.map((el, i) => [distances[i], el]);

      const line = d3
        .line()
        .x((d) => x(d[0]))
        .y((d) => y(d[1]))
        .curve(d3.curveNatural); //d3.curveCardinal is another option

      svg.append("g").attr("class", "line").append("path");

      const drawData = (i: number) => {
        if (i === data.length) {
          return;
        }
        const currentData = data[i];

        state.lineHistory
          ? svg
              .selectAll(".line")
              .append("path")
              .attr("class", `line${i}`)
              .attr("d", line(getCoordinates(currentData)))
              .attr("fill", "none")
              .attr("stroke", "red")
              .transition()
              .duration(1600)
              .ease(d3.easeCubicOut)
              .attr("stroke", "yellow")
              .style("stroke-opacity", "0.0")
          : svg
              .selectAll(".line path")
              .join("path")
              .attr("fill", "none")
              .attr("stroke", "green")
              .transition()
              .duration(16)
              .attr("d", line(getCoordinates(currentData)));

        svg.selectAll(`.line${i - 100}`).remove();

        svg
          .selectAll("circle")
          .data(currentData)
          .join("circle")
          .attr("r", 3.5)
          .attr("cx", (_, i) => x(distances[i]))
          .transition()
          .duration(16)
          .attr("cy", (d) => y(d))
          .on("end", () => drawData(i + 1));
      };
      drawData(0);
    };

    watchEffect(() => {
      const batch = getBatch();
      state.bladesList = batch === undefined ? undefined : getBlades(batch);
    });

    return {
      ...toRefs(state),
      getBlades,
      makeSVGPlot,
    };
  },
});
